import {
    useCreateMessage,
    useDeleteMessage,
    useDeleteThread,
    useFetchNewMessages,
    usePatchMessage,
    usePatchThread,
    useUploadMessageDocuments,
} from 'ClientPortal/src/api/Queries/threads'
import { useIamContext } from 'ClientPortal/src/context/IAmContext'
import { Operation } from 'fast-json-patch'
import { useGetUsers, usePinThread } from 'ClientPortal/src/api/Queries/users'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { useGetExternalParticipants, useGetOrder } from 'ClientPortal/src/api/Queries/orders'
import { assembleAddressFormat, sendToSentry } from 'ComponentLibrary'
import { useGetAsset } from 'ClientPortal/src/api/Queries/assets'
import { useTranslation } from 'react-i18next'

export const useMessageListData = ({ threadId, orderId }: { threadId: UUID; orderId: UUID }) => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.threadListPage' })
    const { patchThread, isPatchingThread } = usePatchThread({ threadId, orderId })
    const { createMessage } = useCreateMessage({ threadId, orderId })
    const { removeMessage } = useDeleteMessage({ threadId, orderId, deletedMessageContent: t('message.deletedMessage') })
    const { patchMessage } = usePatchMessage({ threadId, orderId })
    const { order, isOrderInitialLoading } = useGetOrder({ orderId })

    const { asset } = useGetAsset({ assetId: order?.asset_uuid })

    const { uploadMessageDocuments, failedDocumentUploads } = useUploadMessageDocuments({ threadId })
    const { fetchNewMessages } = useFetchNewMessages()
    const { user: currentUser } = useIamContext()
    const { users } = useGetUsers({ orderId })
    const { externalParticipants, isExternalParticipantsPending } = useGetExternalParticipants({ orderId, threadId })

    const replaceTopic = (topic: string) => {
        const operations: Operation[] = [
            {
                op: 'replace',
                path: '/topic',
                value: topic,
            },
        ]
        patchThread({ operations })
    }

    const replaceParticipantIds = (newParticipantIds: number[]) => {
        const operations: Operation[] = [
            {
                op: 'replace',
                path: '/participant_ids',
                value: newParticipantIds,
            },
        ]
        patchThread({ operations })
    }

    const updateMessage = (messageId: string, content: string, mentions: number[]) => {
        const operations: Operation[] = [
            {
                op: 'replace',
                path: '/content',
                value: content,
            },
            {
                op: 'replace',
                path: '/mentions',
                value: mentions,
            },
        ]
        patchMessage({ messageId, operations })
    }

    const { pinThread, unpinThread, isPatchingUser: isPatchingPin } = usePinThread({ pinnedThreads: currentUser?.pinned_threads ?? [] })

    const downloadDocument = async (messageId: string, documentIndex: number) => {
        try {
            // Since the signed url for the documents are only valid for a few minutes we need to refetch them first
            const response = await fetchNewMessages(threadId)

            const document = response?.messages.find((message) => message.uuid === messageId)?.documents?.[documentIndex]

            if (document) {
                window.open(document.url, '_self')
            }
        } catch (error: unknown) {
            sendToSentry(error)
        }
    }

    const deleteMessage = (messageId: string) => removeMessage({ messageId })

    const { deleteThread, isDeleteThreadPending } = useDeleteThread()

    const orderObservers = order?.observers?.map((id) => users.find((user) => user.uuid === id)).filter((u) => !!u) || []

    return {
        order,
        isOrderInitialLoading,
        replaceTopic,
        replaceParticipantIds,
        createMessage,
        deleteMessage,
        updateMessage,
        isPatchingThread,
        uploadMessageDocuments,
        failedDocumentUploads,
        currentUser,
        downloadDocument,
        users,
        pinThread,
        unpinThread,
        isPatchingPin,
        deleteThread,
        isDeleteThreadPending,
        orderObservers,
        externalParticipantUsers: externalParticipants?.participants || [],
        isExternalParticipantsPending,
        orderLabel: asset?.title || assembleAddressFormat({ street: order?.address.street, house_number: order?.address.house_number }),
    }
}
